<template>
  <div>
    <v-card flat>
      <v-card flat v-if="!readonly">
        <v-autocomplete
          autocomplete="off"
          v-model="itemToAdd"
          :items="itemOptions"
          :item-text="formattedItemName"
          item-value="uuid"
          :search-input.sync="itemSearch"
          color="primary"
          hide-selected
          :placeholder="$t('components.boatItems.boatItemList.addNewItem')"
          return-object
          clearable
          @input.native="loadAutocompleteSearch($route, false)"
          @change="handleAddingNewItem(itemToAdd)"
          no-filter
          :disabled="disableSelector"
        >
          <template v-slot:item="{ item }">
            <span>
              {{ formattedItemName(item) }}
            </span>
            <span v-if="item.sku" class="ml-1">({{ item.sku }}) </span>
            <!-- <span v-if="item.is_readonly" class="ml-1">
              {{ $t("components.boatItems.boatItemList.readOnly") }}
            </span> -->
            <v-spacer></v-spacer>
            <v-icon small class="mr-1" v-if="item.is_custom_part">
              mdi-star-outline
            </v-icon>
            {{ isItemOnBoat(item) }}
          </template>
          <template v-slot:append-item>
            <div v-intersect="infiniteScrollingAutocomplete" />
          </template>
        </v-autocomplete>
      </v-card>
      <v-card flat v-if="readonly">
        <v-autocomplete
          autocomplete="off"
          v-model="itemToAdd"
          :items="itemOptions"
          :item-text="formattedItemName"
          item-value="uuid"
          :search-input.sync="itemSearch"
          color="primary"
          hide-selected
          :placeholder="$t('components.boatItems.boatItemList.search')"
          return-object
          clearable
          @input.native="loadBoatItemsSearch($route, false)"
          @change="openSearchedItem(itemToAdd)"
          no-filter
        >
          <template v-slot:item="{ item }">
            <span>
              {{ formattedItemName(item) }}
            </span>
            <span v-if="item.sku" class="ml-1">({{ item.sku }}) </span>
            <v-spacer></v-spacer>
            <span v-if="item.is_readonly" class="ml-1">
              {{ $t("components.boatItems.boatItemList.readOnly") }}
            </span>
          </template>
          <!-- <template v-slot:append-item>
            <div v-intersect="infiniteScrollingAutocomplete" />
          </template> -->
        </v-autocomplete>
      </v-card>

      <v-row class="boat-items-list--size">
        <v-col cols="10" offset="1">
          <v-row
            v-for="item in filteredBoatItems"
            :key="item.title"
            v-intersect.quiet="{
              handler: infiniteScrolling,
            }"
            :id="item.uuid"
          >
            <!-- <v-col
              cols="12"
              style="cursor: pointer"
              @click="setItemActiveStatus(item)"
            > -->

            <v-row style="cursor: pointer" @click="setItemActiveStatus(item)">
              <v-col cols="1">
                <v-icon
                  v-if="
                    typeof item.prettified_categories !== 'undefined' &&
                    item.prettified_categories.length !== 0 &&
                    typeof item.prettified_categories[0].icon !== 'undefined'
                  "
                  >{{ item.prettified_categories[0].icon }}</v-icon
                >
                <v-tooltip left v-else>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon v-bind="attrs" v-on="on">mdi-help</v-icon>
                  </template>
                  <span>
                    {{
                      $t(
                        "components.boatItems.boatItemList.thisItemDoesNotHaveAStandardizedCategory"
                      )
                    }}
                  </span>
                </v-tooltip>
              </v-col>
              <v-col cols="7">
                <span v-if="item.vendor">{{ item.vendor.name }}</span>
                {{ item.name }} -
                <span v-if="item.sku">({{ item.sku }}) -</span>
                ({{ item.boat_items.length }}x)
              </v-col>
              <v-col cols="3" style="min-width: 0">
                <v-chip
                  v-if="item.is_custom_part"
                  outlined
                  class="float-right"
                  small
                  ><span
                    style="
                      white-space: nowrap;
                      overflow: hidden;
                      text-overflow: ellipsis;
                    "
                    >{{ item.crm_group.name }}</span
                  >
                </v-chip>
              </v-col>
              <v-col cols="1">
                <v-icon class="float-right" v-if="!item.active"
                  >mdi-chevron-down</v-icon
                >
                <v-icon class="float-right" v-else>mdi-chevron-up</v-icon>
              </v-col>
            </v-row>

            <!-- <v-chip class="float-right"> -->
            <!-- <v-icon
                small
                class="mr-1 mt-1 float-right"
                v-if="item.is_custom_part"
              >
              </v-icon> -->
            <!-- Advanced -->
            <!-- </v-chip> -->
            <!-- </v-col> -->
            <keep-alive v-if="item.active">
              <boat-item-list-details
                :readonly="readonly"
                :item="item"
                :disable-adding="disableAdding"
                :disable-deleting="disableDeleting"
                @add="addItem"
                @update="updateItem"
                @delete="deleteItem"
              ></boat-item-list-details>
            </keep-alive>
          </v-row>
        </v-col>
      </v-row>
      <v-row no-gutters v-if="readonly">
        <v-card-text class="d-flex justify-center mb-0 pb-0 mt-0 pt-2">
          ({{ numberOfAvailableBoatItems - filteredBoatItems.length }}
          {{ $t("components.boatItems.boatItemList.moreItems") }}
          )
        </v-card-text>
      </v-row>
    </v-card>
    <v-card class="mt-4" flat v-if="getMissingCategoryText">
      <v-alert dense outlined type="warning" color="grey darken-2">
        <span v-if="initialMissingCategoryText === getMissingCategoryText">
          {{ initialMissingCategoryText }}
        </span>
        <span v-else>
          {{ getMissingCategoryText }}
        </span>
      </v-alert>
    </v-card>
  </div>
</template>

<script>
import api from "@/utils/api";
import categories from "@/utils/categories.js";
import debounce from "@/plugins/debounce";
import { mapGetters } from "vuex";
import snackbar from "@/utils/snackbar";
import BoatItemListDetails from "@/components/boat-items/BoatItemListDetails";

export default {
  components: {
    BoatItemListDetails,
  },
  props: {
    readonly: {
      type: Boolean,
      default: false,
    },
    initialData: {
      type: Object,
      default: Object,
    },
    missingCategories: {
      type: Array,
      default: Array,
    },
    disableSelector: {
      type: Boolean,
      default: false,
    },
    disableAdding: {
      type: Boolean,
      default: false,
    },
    disableDeleting: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      itemSearch: "",
      itemToAdd: null,
      itemOptions: [],
      getUuid: null,
      nextUrl: null,
      getUuidAutocomplete: null,
      nextUrlAutocomplete: null,
      categoryOptions: [],
      verifiedBoatItems: [],
      nonVerifiedBoatItems: [],
      filteredBoatItems: [],
      deletedBoatItems: [],
      countBoatItems: new Map(),
      numberOfAvailableBoatItems: 0,
    };
  },
  created() {
    this.categoryOptions = categories.categoryOptions();

    if (!this.readonly) {
      this.loadAutocompleteSearch(this.$route, true);
    }

    if (typeof this.$route.params.id !== "undefined") {
      this.setBoatItems(this.initialData);
      if (this.readonly) {
        api.getBoatItems(this.$route.params.id, { search: "" }).then((resp) => {
          resp.data.results.forEach((element) => {
            this.itemOptions.push(element);
          });
        });
      }
    } else {
      this.initialData.data.results.forEach((element) => {
        element = this.boatItemTemplate(element);
        this.prettifyCategory(element);
        this.sortPrettifiedCategory(element);
        element.showDetails = false;
        this.isItemVerified(element)
          ? this.verifiedBoatItems.push(element)
          : this.nonVerifiedBoatItems.push(element);
        this.countBoatItems.set(element.uuid, element.boat_items.length);
      });

      this.filteredBoatItems = [
        ...this.verifiedBoatItems,
        ...this.nonVerifiedBoatItems,
      ];

      this.$emit("update:boatItems", [
        ...this.verifiedBoatItems,
        ...this.nonVerifiedBoatItems,
      ]);
    }
  },

  computed: {
    ...mapGetters([
      "getIframeStatus",
      "disableAddingPartsWithoutAdvancedLicenceHandler",
    ]),

    initialMissingCategoryText() {
      const availableCategories = [
        {
          id: "engine",
          value: this.$t("components.boatItems.boatItemList.engine"),
        },
        {
          id: "bilge",
          value: this.$t("components.boatItems.boatItemList.bilge"),
        },
        {
          id: "battery",
          value: this.$t("components.boatItems.boatItemList.battery"),
        },
        {
          id: "display",
          value: this.$t("components.boatItems.boatItemList.display"),
        },
      ];

      let categories = [];

      if (this.missingCategories.length !== 0) {
        categories = availableCategories.filter((category) => {
          return this.missingCategories.includes(category.id);
        });
      }

      let formatedString = this.stringifyCategories(categories);
      if (formatedString.length === 0) {
        return;
      }

      return `${this.$t(
        "components.boatItems.boatItemList.selectA"
      )} ${formatedString} ${this.$t(
        "components.boatItems.boatItemList.toMatchBoatsTelematicsProfile"
      )}`;
    },

    getMissingCategoryText() {
      const availableCategories = [
        {
          id: "engine",
          value: this.$t("components.boatItems.boatItemList.engine"),
          binded: false,
        },
        {
          id: "bilge",
          value: this.$t("components.boatItems.boatItemList.bilge"),
          binded: false,
        },
        {
          id: "battery",
          value: this.$t("components.boatItems.boatItemList.battery"),
          binded: false,
        },
        {
          id: "display",
          value: this.$t("components.boatItems.boatItemList.display"),
          binded: false,
        },
      ];

      let categories = [];

      if (this.verifiedBoatItems.length === 0) {
        return;
      }

      let result = this.verifiedBoatItems
        .filter((item) => typeof item.special === "undefined")
        .flatMap((item) => item.prettified_categories)
        .filter((item) => typeof item.icon !== "undefined")
        .map((item) => item.category);

      categories = availableCategories.filter((item) => {
        return !result.includes(item.id);
      });

      let formatedString = this.stringifyCategories(categories);

      if (formatedString.length === 0) {
        return;
      }

      return `${this.$t(
        "components.boatItems.boatItemList.selectA"
      )} ${formatedString} ${this.$t(
        "components.boatItems.boatItemList.toMatchBoatsTelematicsProfile"
      )}`;
    },
  },

  methods: {
    openSearchedItem(itemToAdd) {
      let scrollIntoView = () => {
        let index = this.findIndexOfItem(this.filteredBoatItems, itemToAdd);
        this.filteredBoatItems[index].active = true;
        this.$set(this.filteredBoatItems, index, this.filteredBoatItems[index]);
        let clusterItemUuid = document.getElementById(itemToAdd.uuid);
        clusterItemUuid.scrollIntoView({
          block: "center",
          inline: "start",
        });
        this.itemToAdd = "undefined";
      };

      if (typeof itemToAdd === "undefined" || itemToAdd === null) {
        return;
      }

      if (
        this.countBoatItems.has(itemToAdd.uuid) &&
        this.countBoatItems.get(itemToAdd.uuid) >= 1
      ) {
        scrollIntoView();
      }
      // else if (!this.countBoatItems.has(itemToAdd.uuid)) {
      //   api
      //     .getBoatItems(this.$route.params.id, {
      //       search: this.itemToAdd.name,
      //     })
      //     .then((resp) => {
      //       this.addItemFromAutocomplete(resp.data.results[0], "exists");
      //     });
      // }
    },

    isItemOnBoat(item) {
      if (typeof item.uuid === "undefined" || item.uuid === null) {
        return;
      }

      if (this.countBoatItems.has(item.uuid)) {
        if (this.countBoatItems.get(item.uuid) >= 1) {
          return this.$t("components.boatItems.boatItemList.show");
        } else {
          return this.$t("components.boatItems.boatItemList.add");
        }
      } else if (!this.countBoatItems.has(item.uuid)) {
        if (item.boat_item_count === 0) {
          return this.$t("components.boatItems.boatItemList.add");
        } else if (typeof item.boat_item_count !== "undefined") {
          return this.$t("components.boatItems.boatItemList.show");
        } else {
          return this.$t("components.boatItems.boatItemList.add");
        }
      }
    },

    setItemActiveStatus(item) {
      let index = this.findIndexOfItem(this.filteredBoatItems, item);
      if (this.filteredBoatItems[index].active === false) {
        this.filteredBoatItems[index].active = true;
        this.$set(this.filteredBoatItems, index, this.filteredBoatItems[index]);
      } else {
        this.filteredBoatItems[index].active = false;
        this.$set(this.filteredBoatItems, index, this.filteredBoatItems[index]);
      }
    },

    stringifyCategories(categories) {
      let formatedString = "";
      categories.forEach((item, index, array) => {
        if (array.length - 2 === index) {
          formatedString += `${item.value} ${this.$t(
            "components.boatItems.boatItemList.and"
          )} `;
        } else if (array.length - 1 === index) {
          formatedString += `${item.value}`;
        } else {
          formatedString += `${item.value}, `;
        }
      });

      return formatedString;
    },
    sortPrettifiedCategory(element) {
      element.prettified_categories.sort((first) => {
        var index = this.categoryOptions
          .map((category) => category.category)
          .findIndex((category) => {
            return category === first.category;
          });
        if (index !== -1) {
          return -1;
        } else {
          return 1;
        }
      });
    },

    prettifyCategory(element) {
      element.prettified_categories = [];
      element.categories.forEach((category) => {
        var prettified_category = this.categoryOptions.find(
          (categoryOption) => category === categoryOption.category
        );
        if (typeof prettified_category !== "undefined") {
          element.prettified_categories.push(prettified_category);
        } else {
          element.prettified_categories.push({
            category: category,
          });
        }
      });
      return element;
    },

    setBoatItems(initialData, from = "local") {
      this.verifiedBoatItems = [];
      this.nonVerifiedBoatItems = [];
      let initData =
        from === "external" ? initialData : initialData.data.results;

      this.numberOfAvailableBoatItems =
        from === "external" ? 0 : initialData.data.count;
      initData.forEach((element) => {
        element.boat_items = element.boat_items.map((boat_item) => {
          return { ...boat_item, crm_group: element.crm_group };
        });

        this.prettifyCategory(element);
        this.sortPrettifiedCategory(element);
        element.active = false;
        this.isItemVerified(element)
          ? this.verifiedBoatItems.push(element)
          : this.nonVerifiedBoatItems.push(element);
        this.countBoatItems.set(element.uuid, element.boat_items.length);
      });
      this.filteredBoatItems = [
        ...this.verifiedBoatItems,
        ...this.nonVerifiedBoatItems,
      ];
      this.$emit("update:boatItems", [
        ...this.verifiedBoatItems,
        ...this.nonVerifiedBoatItems,
      ]);

      from == "local" && this.handleNextPage(initialData);
    },

    findVerifiedBoatItems(array) {
      return array.findIndex((item) => {
        return !item.prettified_categories.some(
          (category) => typeof category.icon !== "undefined"
        );
      });
    },

    isItemVerified(element) {
      return element.prettified_categories.some(
        (category) => typeof category.icon !== "undefined"
      );
    },

    handleNextPage(resp, autocomplete = false) {
      if (autocomplete) {
        if (resp.data.next) {
          this.getUuidAutocomplete =
            resp.data.results[resp.data.results.length - 12].uuid;
          this.nextUrlAutocomplete = resp.data.next;
        } else {
          this.getUuidAutocomplete = null;
        }
      } else {
        if (resp.data.next) {
          this.getUuid = resp.data.results[resp.data.results.length - 12].uuid;
          this.nextUrl = resp.data.next;
        } else {
          this.getUuid = null;
        }
      }
    },

    infiniteScrolling(entries, observer, isIntersecting) {
      if (entries[0].target.id === this.getUuid && isIntersecting) {
        // LINK PARSER, remove base of url
        let path = this.nextUrl.split("/").splice(5, 6).join("/");
        api.getBoatItemsNextPage(path).then((resp) => {
          this.handleNextPage(resp);
          resp.data.results.forEach((element) => {
            this.prettifyCategory(element);
            this.sortPrettifiedCategory(element);
            element.active = false;
            if (!this.countBoatItems.has(element.uuid)) {
              this.countBoatItems.set(element.uuid, element.boat_items.length);
              if (this.isItemVerified(element)) {
                this.verifiedBoatItems.push(element);
              } else {
                this.nonVerifiedBoatItems.push(element);
              }
            }
          });
          this.filteredBoatItems = [
            ...this.verifiedBoatItems,
            ...this.nonVerifiedBoatItems,
          ];
        });
      }
    },

    infiniteScrollingAutocomplete(entries, observer, isIntersecting) {
      if (isIntersecting && this.nextUrlAutocomplete) {
        let path = this.nextUrlAutocomplete.split("/").splice(5, 5).join("/");
        api.getItemNextPage(path).then((resp) => {
          this.handleNextPage(resp, true);
          const results = resp.data.results.map((item) => {
            return {
              ...item,
              disabled: this.disableAddingPartsWithoutAdvancedLicenceHandler(
                "add-part-on-boat"
              )(item.crm_group)(item, "add").disabled,
            };
          });
          this.itemOptions = [...this.itemOptions, ...results];
        });
      }
    },

    formattedItemName: (item) => {
      if (item.vendor) {
        return item.vendor.name + " " + item.name;
      } else {
        return item.name;
      }
    },

    findIndexOfItem(array, boatItem) {
      return array.findIndex((item) => item.uuid === boatItem.uuid);
    },

    findClusterItem(array, boatItem) {
      return array.find((item) => item.uuid === boatItem.uuid);
    },

    deleteItem(boatItem, index, clusterItemIndex) {
      this.deletedBoatItems.push({
        ...boatItem.boat_items[index],
        crm_item_uuid: boatItem.boat_items[index].uuid,
      });

      if (this.isItemVerified(boatItem)) {
        if (boatItem.boat_items.length === 1) {
          this.verifiedBoatItems.splice(
            this.findIndexOfItem(this.verifiedBoatItems, boatItem),
            1
          );
          this.filteredBoatItems.splice(
            this.findIndexOfItem(this.filteredBoatItems, boatItem),
            1
          );
        } else {
          this.verifiedBoatItems[
            this.findIndexOfItem(this.verifiedBoatItems, boatItem)
          ].boat_items.splice(index, 1);
        }
      } else {
        if (boatItem.boat_items.length === 1) {
          this.nonVerifiedBoatItems.splice(
            this.findIndexOfItem(this.nonVerifiedBoatItems, boatItem),
            1
          );
          this.filteredBoatItems.splice(
            this.findIndexOfItem(this.filteredBoatItems, boatItem),
            1
          );
        } else {
          this.nonVerifiedBoatItems[
            this.findIndexOfItem(this.nonVerifiedBoatItems, boatItem)
          ].boat_items.splice(index, 1);
        }
      }

      let indexToAddInFilteredBoatItems = this.filteredBoatItems.findIndex(
        (item) => item.uuid === boatItem.uuid
      );

      this.$set(
        this.filteredBoatItems,
        this.filteredBoatItems[indexToAddInFilteredBoatItems],
        boatItem
      );

      if (
        this.countBoatItems.has(boatItem.uuid) &&
        this.countBoatItems.get(boatItem.uuid) > 1
      ) {
        this.countBoatItems.set(
          boatItem.uuid,
          this.countBoatItems.get(boatItem.uuid) - 1
        );
      } else {
        this.countBoatItems.set(boatItem.uuid, 0);
      }

      this.$emit("update:boatItems", [
        ...this.verifiedBoatItems,
        ...this.nonVerifiedBoatItems,
      ]);
      this.$emit("update:deletedBoatItems", this.deletedBoatItems);
    },

    updateItem(boatItem, index, event) {
      if (typeof boatItem.boat_items[index].action === "undefined") {
        if (this.isItemVerified(boatItem)) {
          this.verifiedBoatItems[
            this.findIndexOfItem(this.verifiedBoatItems, boatItem)
          ].boat_items[index].action = "updated";
        } else {
          this.nonVerifiedBoatItems[
            this.findIndexOfItem(this.nonVerifiedBoatItems, boatItem)
          ].boat_items[index].action = "updated";
        }
      }
      if (this.isItemVerified(boatItem)) {
        this.verifiedBoatItems[
          this.findIndexOfItem(this.verifiedBoatItems, boatItem)
        ].boat_items[index].serial_number = event;
      } else {
        this.nonVerifiedBoatItems[
          this.findIndexOfItem(this.nonVerifiedBoatItems, boatItem)
        ].boat_items[index].serial_number = event;
      }

      this.$emit("update:boatItems", [
        ...this.verifiedBoatItems,
        ...this.nonVerifiedBoatItems,
      ]);
    },

    boatItemTemplate(element, status = true) {
      element.action = "created";
      element.serial_number = "";
      return {
        categories: element.categories,
        boat_items: status
          ? [{ ...element, is_editable: true, is_removable: true }]
          : [],
        documents: element.documents,
        description: element.description,
        name: element.name,
        sku: element.sku,
        vendor: element.vendor,
        uuid: element.uuid,
        action: "created",
        crm_group: element.crm_group,
      };
    },

    //THIS IS MADE FOR ADDING ITEMS FROM INLINE
    addItem(clusterItem) {
      if (this.isItemVerified(clusterItem)) {
        let indexToAddInVerifiedBoatItems = this.findIndexOfItem(
          this.verifiedBoatItems,
          clusterItem
        );
        this.$set(
          this.verifiedBoatItems[indexToAddInVerifiedBoatItems].boat_items,
          this.verifiedBoatItems[indexToAddInVerifiedBoatItems].boat_items
            .length,
          { ...clusterItem.boat_items[0], action: "created", serial_number: "" }
        );
      } else {
        let indexToAddInNonVerifiedBoatItems = this.findIndexOfItem(
          this.nonVerifiedBoatItems,
          clusterItem
        );
        this.$set(
          this.nonVerifiedBoatItems[indexToAddInNonVerifiedBoatItems]
            .boat_items,
          this.nonVerifiedBoatItems[indexToAddInNonVerifiedBoatItems].boat_items
            .length,
          { ...clusterItem.boat_items[0], action: "created", serial_number: "" }
        );
      }

      if (
        this.countBoatItems.has(clusterItem.uuid) &&
        this.countBoatItems.get(clusterItem.uuid) >= 1
      ) {
        this.countBoatItems.set(
          clusterItem.uuid,
          this.countBoatItems.get(clusterItem.uuid) + 1
        );
      } else {
        this.countBoatItems.set(
          clusterItem.uuid,
          clusterItem.boat_items.length
        );
      }

      snackbar.success(
        this.$t("utils.snackbar.success.successfullyAdded"),
        false
      );

      this.$emit("update:boatItems", [
        ...this.verifiedBoatItems,
        ...this.nonVerifiedBoatItems,
      ]);
    },

    handleAddingNewItem(itemToAdd) {
      let scrollIntoView = () => {
        let index = this.findIndexOfItem(this.filteredBoatItems, itemToAdd);
        this.filteredBoatItems[index].active = true;
        this.$set(this.filteredBoatItems, index, this.filteredBoatItems[index]);
        let clusterItemUuid = document.getElementById(itemToAdd.uuid);
        clusterItemUuid.scrollIntoView({
          block: "center",
          inline: "start",
        });
      };

      if (typeof itemToAdd === "undefined" || itemToAdd === null) {
        return;
      }

      if (typeof this.$route.params.id === "undefined") {
        if (
          this.countBoatItems.has(itemToAdd.uuid) &&
          this.countBoatItems.get(itemToAdd.uuid) >= 1
        ) {
          scrollIntoView();
          setTimeout(() => {
            this.itemToAdd = "undefined";
          }, 0);
        } else {
          this.addItemFromAutocomplete(itemToAdd);
        }
      } else {
        if (
          this.countBoatItems.has(itemToAdd.uuid) &&
          this.countBoatItems.get(itemToAdd.uuid) >= 1
        ) {
          scrollIntoView();
          setTimeout(() => {
            this.itemToAdd = "undefined";
          }, 0);
        } else if (
          !this.countBoatItems.has(itemToAdd.uuid) ||
          (this.countBoatItems.has(itemToAdd.uuid) &&
            this.countBoatItems.get(itemToAdd.uuid) === 0)
        ) {
          api
            .getBoatItems(this.$route.params.id, {
              search: this.itemToAdd.name,
            })
            .then((resp) => {
              const index = resp.data.results.findIndex(
                (element) => element.uuid === itemToAdd.uuid
              );
              if (index !== -1) {
                this.addItemFromAutocomplete(
                  resp.data.results[index],
                  "exists"
                );
              } else {
                this.addItemFromAutocomplete(itemToAdd);
              }
            });
        }
      }
    },

    //THIS IS MADE FOR ADDING SELECTED ITEMS FROM AUTOCOMPLETE AND FROM CREATE ITEM IN BOATSADDEDIT
    addItemFromAutocomplete(itemToAdd, status = "notExists") {
      let scrollIntoView = () => {
        let clusterItemUuid = document.getElementById(itemToAdd.uuid);
        clusterItemUuid.scrollIntoView({
          block: "center",
          inline: "start",
        });
      };

      let template = {};

      if (status === "notExists") {
        template = this.boatItemTemplate(itemToAdd);
      } else {
        template = itemToAdd;
      }
      this.prettifyCategory(template);
      this.sortPrettifiedCategory(template);

      if (this.isItemVerified(template)) {
        let indexToAddInVerifiedBoatItems = this.findIndexOfItem(
          this.verifiedBoatItems,
          itemToAdd
        );
        if (indexToAddInVerifiedBoatItems !== -1) {
          this.verifiedBoatItems[indexToAddInVerifiedBoatItems].active = true;
          this.$set(
            this.verifiedBoatItems[indexToAddInVerifiedBoatItems].boat_items,
            this.verifiedBoatItems[indexToAddInVerifiedBoatItems].boat_items
              .length,
            { ...itemToAdd, serial_number: "" }
          );
        } else {
          template.active = true;
          this.$set(
            this.verifiedBoatItems,
            this.verifiedBoatItems.length,
            template
          );
        }
      } else {
        let indexToAddInNonVerifiedBoatItems = this.findIndexOfItem(
          this.nonVerifiedBoatItems,
          itemToAdd
        );
        if (indexToAddInNonVerifiedBoatItems !== -1) {
          this.nonVerifiedBoatItems[
            indexToAddInNonVerifiedBoatItems
          ].active = true;
          this.$set(
            this.nonVerifiedBoatItems[indexToAddInNonVerifiedBoatItems]
              .boat_items,
            this.nonVerifiedBoatItems[indexToAddInNonVerifiedBoatItems]
              .boat_items.length,
            { ...itemToAdd, serial_number: "" }
          );
        } else {
          template.active = true;
          this.$set(
            this.nonVerifiedBoatItems,
            this.nonVerifiedBoatItems.length,
            template
          );
        }
      }

      this.filteredBoatItems = [
        ...this.verifiedBoatItems,
        ...this.nonVerifiedBoatItems,
      ];

      setTimeout(() => {
        scrollIntoView();
        this.itemToAdd = "undefined";
      }, 0);

      if (
        this.countBoatItems.has(itemToAdd.uuid) &&
        this.countBoatItems.get(itemToAdd.uuid) >= 1
      ) {
        this.countBoatItems.set(
          itemToAdd.uuid,
          this.countBoatItems.get(itemToAdd.uuid) + 1
        );
      } else {
        this.countBoatItems.set(template.uuid, template.boat_items.length);
      }

      if (status === "notExists") {
        snackbar.success(
          this.$t("utils.snackbar.success.successfullyAdded"),
          false
        );
      }

      this.$emit("update:boatItems", [
        ...this.verifiedBoatItems,
        ...this.nonVerifiedBoatItems,
      ]);
    },

    loadAutocompleteSearch(route, initalLoad) {
      let fetchParts = () => {
        return new Promise((resolve, reject) => {
          this.itemSearch = this.itemSearch ? this.itemSearch : "";
          let boatId =
            typeof route.params.id === "undefined" ? "" : route.params.id;
          api
            .getItemList({ crm_boat_uuid: boatId, search: this.itemSearch })
            .then((resp) => {
              this.handleNextPage(resp, true);
              this.itemOptions = resp.data.results.map((item) => {
                return {
                  ...item,
                  disabled:
                    this.disableAddingPartsWithoutAdvancedLicenceHandler(
                      "add-part-on-boat"
                    )(item.crm_group)(item, "add").disabled,
                };
              });
              resolve();
            });
        });
      };

      if (initalLoad) return fetchParts();

      if (this.timeout) clearTimeout(this.timeout);

      this.timeout = setTimeout(() => {
        return fetchParts();
      }, 300);
    },

    loadBoatItemsSearch(route, initalLoad) {
      let fetchParts = () => {
        return new Promise((resolve, reject) => {
          this.itemSearch = this.itemSearch ? this.itemSearch : "";
          api
            .getBoatItems(route.params.id, { search: this.itemSearch })
            .then((resp) => {
              this.handleNextPage(resp, true);
              this.itemOptions = resp.data.results.map((item) => {
                return {
                  ...item,
                  // disabled:
                  //   this.disableAddingPartsWithoutAdvancedLicenceHandler(
                  //     "add-part-on-boat"
                  //   )(item.crm_group)(item, "details").disabled,
                };
              });
            });
          resolve();
        });
      };

      if (initalLoad) return fetchParts();

      if (this.timeout) clearTimeout(this.timeout);

      this.timeout = setTimeout(() => {
        return fetchParts();
      }, 300);
    },
  },
};
</script>

<style scoped>
.boat-items-list--size {
  max-height: 600px;
  overflow-y: auto;
}
.overflow-y--enabled {
  overflow-y: scroll;
}
.overflow-y--disabled {
  overflow-y: hidden;
}
</style>
